import { Flex } from "@chakra-ui/layout";
import { Box, Skeleton, Text } from "@chakra-ui/react";
import { Col, Empty, Row, Spin } from "antd";
import { FiltroCobranca } from "core/features/cobranca/typings";
import colors from "core/resources/theme/colors";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import InfiniteScroll from "react-infinite-scroll-component";
import CardStatusCobranca from "./../CardStatusCobranca";
import useKambanCobrancas from "./hooks/useKambanCobrancas";
import { tagSetorJuridicoString } from "features/conta/typings";

interface ListagemCobrancasProps {
  filtroCobranca: FiltroCobranca;
}

const Loader = () => (
  <Box justifyContent="center" alignItems="center" d="flex" flex={1} my="16px">
    <Flex gap="16px">
      <Spin />
      Carregando...
    </Flex>
  </Box>
);

export default function ListagemCobrancas({
  filtroCobranca,
}: ListagemCobrancasProps) {
  const {
    aCobrar,
    emCobranca,
    emJuridico,
    unidadeAjuizada,
    ajuizada,
    andamento,
    cobrar,
    juridico,
    fetchNextPageACobrar,
    fetchNextPageAndamento,
    fetchNextPageJuridico,
    fetchNextPageAjuizada,
    isDragging,
    hasNextPageACobrar,
    hasNextPageJuridico,
    hasNextPageAndamento,
    hasNextPageAjuizada,
    isLoadingACobrar,
    isLoadingAndamento,
    isLoadingJuridico,
    isLoadingAjuizada,
    onDragEnd,
    setIsDragging,
    isFetchingNextPageACobrar,
    isFetchingNextPageAndamento,
    isFetchingNextPageJuridico,
    isFetchingNextPageAjuizada,
  } = useKambanCobrancas({ filtroCobranca });

  const totalCobrar = cobrar?.pages?.[0].meta.totalItems;
  const totalAndamento = andamento?.pages?.[0].meta.totalItems;
  const totalJuridico = juridico?.pages?.[0].meta.totalItems;
  const totalAjuizada = unidadeAjuizada?.pages?.[0].meta.totalItems;

  return (
    <DragDropContext
      onDragEnd={onDragEnd}
      onDragStart={() => {
        setIsDragging(true);
      }}
    >
      <Row gutter={[8, 24]}>
        <Box as={Col} xs={12} lg={6}>
          <Text fontWeight="medium" fontSize={16} mb={4}>
            A Cobrar ({totalCobrar})
          </Text>

          <Box
            maxH="80vh"
            id="infinite-scroll-a-cobrar"
            overflowY="auto"
            className="cobranca-scroll-bar"
          >
            <InfiniteScroll
              dataLength={aCobrar.length}
              style={{
                overflowX: "hidden",
                paddingRight: 8,
              }}
              next={fetchNextPageACobrar}
              hasMore={!!hasNextPageACobrar}
              scrollableTarget="infinite-scroll-a-cobrar"
              loader={isFetchingNextPageACobrar ? <Loader /> : null}
            >
              <Droppable droppableId={"aCobrar"}>
                {(provided, snapshot) => (
                  <Box
                    rounded={10}
                    p={4}
                    px={1}
                    bg={isDragging ? `${colors.gray50}BF` : colors.gray50}
                    ref={provided.innerRef}
                    borderWidth={2}
                    borderColor={isDragging ? colors.primary : "transparent"}
                    borderStyle="dashed"
                    minHeight={166}
                  >
                    {isLoadingACobrar ? (
                      <>
                        <Skeleton w="100%" h="200px" />
                      </>
                    ) : null}

                    {!isLoadingACobrar && !aCobrar?.length ? (
                      <Empty description="Sem inadimplentes" />
                    ) : null}

                    {!isLoadingACobrar && aCobrar?.length ? (
                      <>
                        {aCobrar?.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <CardStatusCobranca
                                  key={item.id}
                                  item={item}
                                  colorStatus={colors.red}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                      </>
                    ) : null}

                    {snapshot.isDraggingOver ? provided.placeholder : null}
                  </Box>
                )}
              </Droppable>
            </InfiniteScroll>
          </Box>
        </Box>

        <Box as={Col} xs={12} lg={6}>
          <Text fontWeight="medium" fontSize={16} mb={4}>
            Em Cobrança ({totalAndamento})
          </Text>
          <Box
            maxH="80vh"
            id="infinite-scroll-em-cobranca"
            overflowY="auto"
            className="cobranca-scroll-bar"
          >
            <InfiniteScroll
              dataLength={emCobranca.length}
              style={{
                overflowX: "hidden",
                paddingRight: 8,
              }}
              next={fetchNextPageAndamento}
              hasMore={!!hasNextPageAndamento}
              loader={isFetchingNextPageAndamento ? <Loader /> : null}
              scrollableTarget="infinite-scroll-em-cobranca"
            >
              <Droppable droppableId="emCobranca">
                {(provided, snapshot) => (
                  <Box
                    rounded={10}
                    p={4}
                    bg={isDragging ? `${colors.gray50}BF` : colors.gray50}
                    ref={provided.innerRef}
                    borderWidth={2}
                    borderColor={isDragging ? colors.primary : "transparent"}
                    borderStyle="dashed"
                    minHeight={166}
                  >
                    {isLoadingAndamento ? (
                      <>
                        <Skeleton w="100%" h="250px" />
                      </>
                    ) : null}

                    {!isLoadingAndamento && !emCobranca?.length ? (
                      <Empty description="Sem unidades em cobrança" />
                    ) : null}

                    {!isLoadingAndamento && emCobranca?.length ? (
                      <>
                        {emCobranca?.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id}
                            index={index}
                          >
                            {(provided) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <CardStatusCobranca
                                  key={item.id}
                                  item={item}
                                  colorStatus={colors.green}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                      </>
                    ) : null}
                    {snapshot.isDraggingOver ? provided.placeholder : null}
                  </Box>
                )}
              </Droppable>
            </InfiniteScroll>
          </Box>
        </Box>

        <Box as={Col} xs={12} lg={6}>
          <Text fontWeight="medium" fontSize={16} mb={4}>
            Em {tagSetorJuridicoString} ({totalJuridico})
          </Text>
          <Box
            maxH="80vh"
            id="infinite-scroll-em-juridico"
            overflowY="auto"
            className="cobranca-scroll-bar"
          >
            <InfiniteScroll
              dataLength={emJuridico.length}
              style={{
                overflowX: "hidden",
                paddingRight: 8,
              }}
              next={fetchNextPageJuridico}
              hasMore={!!hasNextPageJuridico}
              scrollableTarget="infinite-scroll-em-juridico"
              loader={isFetchingNextPageJuridico ? <Loader /> : null}
            >
              <Box rounded={10} p={4} bg={colors.gray50}>
                {isLoadingJuridico ? (
                  <>
                    <Skeleton w="100%" h="200px" />
                  </>
                ) : null}

                {!isLoadingJuridico && !emJuridico?.length ? (
                  <Empty description="Sem unidades em jurídico" />
                ) : null}

                {!isLoadingJuridico && emJuridico?.length ? (
                  <>
                    {emJuridico?.map((item) => (
                      <Box>
                        <CardStatusCobranca
                          key={item.id}
                          item={item}
                          colorStatus={colors.black}
                        />
                      </Box>
                    ))}
                  </>
                ) : null}
              </Box>
            </InfiniteScroll>
          </Box>
        </Box>

        <Box as={Col} xs={12} lg={6}>
          <Text fontWeight="medium" fontSize={16} mb={4}>
            Ajuizada ({totalAjuizada})
          </Text>

          <Box
            maxH="80vh"
            id="infinite-scroll-ajuizada"
            overflowY="auto"
            className="cobranca-scroll-bar"
          >
            <InfiniteScroll
              dataLength={ajuizada.length}
              style={{
                overflowX: "hidden",
                paddingRight: 8,
              }}
              next={fetchNextPageAjuizada}
              hasMore={!!hasNextPageAjuizada}
              scrollableTarget="infinite-scroll-ajuizada"
              loader={isFetchingNextPageAjuizada ? <Loader /> : null}
            >
              <Droppable droppableId={"ajuizada"}>
                {(provided, snapshot) => (
                  <Box
                    rounded={10}
                    p={4}
                    bg={isDragging ? `${colors.gray50}BF` : colors.gray50}
                    ref={provided.innerRef}
                    borderWidth={2}
                    borderColor={isDragging ? colors.primary : "transparent"}
                    borderStyle="dashed"
                    minHeight={166}
                  >
                    {isLoadingAjuizada ? (
                      <>
                        <Skeleton w="100%" h="200px" />
                      </>
                    ) : null}

                    {!isLoadingAjuizada && !ajuizada?.length ? (
                      <Empty description="Sem inadimplentes" />
                    ) : null}

                    {!isLoadingAjuizada && ajuizada?.length ? (
                      <>
                        {ajuizada?.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <CardStatusCobranca
                                  key={item.id}
                                  item={item}
                                  colorStatus={colors.yellow}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))}
                      </>
                    ) : null}

                    {snapshot.isDraggingOver ? provided.placeholder : null}
                  </Box>
                )}
              </Droppable>
            </InfiniteScroll>
          </Box>
        </Box>
      </Row>
    </DragDropContext>
  );
}
